import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, ShieldCheck, ShieldAlert, Send, Settings2, ScrollText } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm";
const label = "text-xs font-semibold text-[var(--ink-2)] uppercase tracking-wide mb-1 block";

function StatusPill({ status }) {
  const map = { sent: "bg-[#E8F5E9] text-[#1B5E20]", held: "bg-[#FFF3E0] text-[#E65100]", failed: "bg-[#FFEBEE] text-[#B71C1C]" };
  return <span className={`text-[11px] font-semibold px-2 py-0.5 ${map[status] || "bg-[var(--alt)] text-[var(--ink-2)]"}`}>{status}</span>;
}

function GlobalSettings() {
  const [s, setS] = useState(null);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/admin/email-settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);

  const save = async (extra = {}) => {
    try {
      const payload = { ...s, ...extra,
        reminder_days: typeof s.reminder_days === "string" ? s.reminder_days.split(",").map((x) => parseInt(x.trim(), 10)).filter(Boolean) : s.reminder_days };
      const { data } = await api.put("/admin/email-settings", payload);
      setS(data); toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const test = async () => { const { data } = await api.post("/admin/email-test", {}); toast[data.status === "sent" ? "success" : "info"](`Test email ${data.status}`); };

  if (!s) return <p className="text-[var(--ink-2)]">Loading…</p>;
  const days = Array.isArray(s.reminder_days) ? s.reminder_days.join(", ") : s.reminder_days;

  return (
    <div className="space-y-6" data-testid="global-email-settings">
      <div className={`flex items-center gap-3 p-4 border ${s.brevo_connected ? "border-[#1B5E20] bg-[#E8F5E9]" : "border-[#E65100] bg-[#FFF3E0]"}`}>
        {s.brevo_connected ? <ShieldCheck className="text-[#1B5E20]" /> : <ShieldAlert className="text-[#E65100]" />}
        <div className="text-sm">
          <div className="font-semibold">{s.brevo_connected ? "Brevo connected" : "Brevo API key not yet configured"}</div>
          <div className="text-[var(--ink-2)]">{s.brevo_connected ? "Ready to send once you enable sending below." : "Add the BREVO_API_KEY secret to connect. Sending stays off until then."}</div>
        </div>
      </div>

      <div className="border border-[var(--line)] bg-white p-5 space-y-4">
        <h3 className="font-display font-bold flex items-center gap-2"><Settings2 size={16} /> Sender & branding</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={label}>Sender name</label><input data-testid="es-sender-name" value={s.sender_name || ""} onChange={(e) => set("sender_name", e.target.value)} className={inputCls} /></div>
          <div><label className={label}>Sender email</label><input data-testid="es-sender-email" value={s.sender_email || ""} onChange={(e) => set("sender_email", e.target.value)} className={inputCls} /></div>
          <div><label className={label}>Reply-to email</label><input data-testid="es-reply-to" value={s.reply_to || ""} onChange={(e) => set("reply_to", e.target.value)} className={inputCls} /></div>
          <div><label className={label}>Support email</label><input data-testid="es-support" value={s.support_email || ""} onChange={(e) => set("support_email", e.target.value)} className={inputCls} /></div>
          <div><label className={label}>Platform domain</label><input data-testid="es-domain" value={s.platform_domain || ""} onChange={(e) => set("platform_domain", e.target.value)} className={inputCls} /></div>
          <div><label className={label}>Logo URL</label><input data-testid="es-logo" value={s.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className={inputCls} placeholder="https://…" /></div>
        </div>
        <div><label className={label}>Email footer</label><textarea data-testid="es-footer" value={s.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} rows={2} className={inputCls} /></div>
      </div>

      <div className="border border-[var(--line)] bg-white p-5 space-y-4">
        <h3 className="font-display font-bold">Reminders</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><label className={label}>Reminder days before</label><input data-testid="es-reminder-days" value={days} onChange={(e) => set("reminder_days", e.target.value)} className={inputCls} placeholder="14, 7, 1" /></div>
          <div><label className={label}>Morning-of time</label><input data-testid="es-morning-time" value={s.morning_of_time || ""} onChange={(e) => set("morning_of_time", e.target.value)} className={inputCls} placeholder="08:00" /></div>
          <div><label className={label}>Timezone</label><input data-testid="es-timezone" value={s.timezone || ""} onChange={(e) => set("timezone", e.target.value)} className={inputCls} /></div>
        </div>
      </div>

      <div className="border border-[var(--line)] bg-white p-5">
        <h3 className="font-display font-bold mb-3">Go-live</h3>
        <label className="flex items-center gap-3 text-sm cursor-pointer" data-testid="es-domain-auth">
          <input type="checkbox" checked={!!s.domain_authenticated} onChange={(e) => set("domain_authenticated", e.target.checked)} className="accent-[var(--accent)]" />
          Domain authenticated in Brevo (SPF + DKIM green)
        </label>
        <label className="flex items-center gap-3 text-sm cursor-pointer mt-3" data-testid="es-sending-enabled">
          <input type="checkbox" checked={!!s.sending_enabled} onChange={(e) => set("sending_enabled", e.target.checked)} disabled={!s.brevo_connected} className="accent-[var(--accent)]" />
          Enable live email sending {(!s.brevo_connected) && <span className="text-[var(--ink-2)]">(needs Brevo key)</span>}
        </label>
        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={() => save()} data-testid="es-save" className="bg-[var(--accent)] text-white font-semibold px-5 py-2.5 hover:bg-[var(--accent-hover)] text-sm">Save settings</button>
          <button onClick={test} data-testid="es-test" className="border border-[var(--ink)] font-semibold px-5 py-2.5 hover:bg-[var(--ink)] hover:text-white text-sm inline-flex items-center gap-2"><Send size={14} /> Send test email</button>
        </div>
      </div>
    </div>
  );
}

function ProviderSettings() {
  const [s, setS] = useState({});
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  useEffect(() => { api.get("/provider/email-settings").then((r) => setS(r.data)); }, []);
  const save = async () => { const { data } = await api.put("/provider/email-settings", s); setS(data); toast.success("Saved"); };
  return (
    <div className="border border-[var(--line)] bg-white p-5 space-y-4" data-testid="provider-email-settings">
      <h3 className="font-display font-bold">Your email branding</h3>
      <p className="text-sm text-[var(--ink-2)]">Override the reply-to address, footer note and logo for emails about your courses. Global sender settings and the Brevo connection are managed by the platform admin.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={label}>Reply-to email</label><input data-testid="pes-reply-to" value={s.reply_to || ""} onChange={(e) => set("reply_to", e.target.value)} className={inputCls} /></div>
        <div><label className={label}>Logo URL</label><input data-testid="pes-logo" value={s.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} className={inputCls} placeholder="https://…" /></div>
      </div>
      <div><label className={label}>Footer note</label><textarea data-testid="pes-footer" value={s.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} rows={2} className={inputCls} /></div>
      <button onClick={save} data-testid="pes-save" className="bg-[var(--accent)] text-white font-semibold px-5 py-2.5 hover:bg-[var(--accent-hover)] text-sm">Save</button>
    </div>
  );
}

function EmailLog() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/email-logs").then((r) => setLogs(r.data)); }, []);
  return (
    <div className="border border-[var(--line)] bg-white overflow-x-auto" data-testid="email-log-table">
      <table className="w-full text-sm">
        <thead className="bg-[var(--alt)] text-left text-xs uppercase tracking-wide text-[var(--ink-2)]">
          <tr><th className="px-4 py-3">Recipient</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Course</th><th className="px-4 py-3">Sent</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Detail</th></tr>
        </thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-t border-[var(--line)]">
              <td className="px-4 py-3">{l.to_email}</td>
              <td className="px-4 py-3">{l.kind}{l.resent && <span className="ml-1 text-[10px] text-[var(--accent)]">(resent)</span>}</td>
              <td className="px-4 py-3 text-[var(--ink-2)]">{l.course_title || "—"}</td>
              <td className="px-4 py-3 text-[var(--ink-2)]">{new Date(l.created_at).toLocaleString("en-GB")}</td>
              <td className="px-4 py-3"><StatusPill status={l.status} /></td>
              <td className="px-4 py-3 text-xs text-[var(--ink-2)] max-w-[220px] truncate">{l.failure_reason || l.message_id || ""}</td>
            </tr>
          ))}
          {logs.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--ink-2)]">No emails logged yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function EmailAdmin() {
  const { user } = useAuth();
  const [view, setView] = useState("settings");
  if (user?.role === "tutor") return <p className="text-[var(--ink-2)]">Email settings are managed by administrators.</p>;
  return (
    <div data-testid="email-admin">
      <div className="flex gap-2 mb-5">
        <button onClick={() => setView("settings")} data-testid="ea-tab-settings" className={`text-sm font-semibold px-4 py-2 inline-flex items-center gap-1.5 ${view === "settings" ? "bg-[var(--ink)] text-white" : "border border-[var(--line)]"}`}><Mail size={14} /> Settings</button>
        <button onClick={() => setView("logs")} data-testid="ea-tab-logs" className={`text-sm font-semibold px-4 py-2 inline-flex items-center gap-1.5 ${view === "logs" ? "bg-[var(--ink)] text-white" : "border border-[var(--line)]"}`}><ScrollText size={14} /> Delivery log</button>
      </div>
      {view === "settings" ? (user.role === "platform_admin" ? <GlobalSettings /> : <ProviderSettings />) : <EmailLog />}
    </div>
  );
}
